import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { type ComponentProps } from "react";
import { type RouteComponentProps } from "react-router";

import { type MediaPropertiesTab, MediaType } from "types/enum";

import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { type TemplateType } from "handlers/models/templateType";

const Photo = lazyLoadComponent(() => import("entity/photo/Photo/Photo"));
const Video = lazyLoadComponent(() => import("entity/video/Video/Video"));
const Document = lazyLoadComponent(() => import("entity/document/Document/Document"));

interface IMediaRouteParams {
    mediaType: string;
    entityId: string;
    activeTab?: string;
    docInstanceId?: string;
    folderId?: string;
    jobId?: string;
    templateDocumentInstanceId?: string;
    templateType?: string;
}

interface IRouteMediaPropertiesProps
    extends Omit<
        ComponentProps<typeof Video>,
        "documentInstanceId" | "jobId" | "history" | "location" | "match" | "initialActiveTab"
    > {
    entityName?: string;
    handleUpdateDocumentInstance?: (
        documentInstanceId: number,
        updatedProperties: Partial<BTFileSystem>,
    ) => void;
    onDocumentDeleted?: (documentInstanceId: number) => void;
}

const getComponent = (
    props: IRouteMediaPropertiesProps,
    routeProps: RouteComponentProps<IMediaRouteParams>,
) => {
    const {
        docInstanceId,
        folderId,
        jobId,
        mediaType,
        templateDocumentInstanceId,
        templateType,
        activeTab,
    } = routeProps.match.params as {
        docInstanceId: string;
        folderId: string;
        jobId: string;
        mediaType: string;
        templateDocumentInstanceId: string;
        templateType: string;
        activeTab: string;
    };

    const callingFromEntityId = parseInt(routeProps.match.params.entityId);
    const mediaTypeParam = parseInt(mediaType) as MediaType;
    const activeTabParam = parseInt(activeTab);
    const initialActiveTab = !isNaN(activeTabParam)
        ? (activeTabParam as MediaPropertiesTab)
        : undefined;
    const templateTypeParam = parseInt(templateType) as TemplateType;

    switch (mediaTypeParam) {
        case MediaType.Document:
            const { handler, ...documentProps } = props;
            return (
                <Document
                    {...documentProps}
                    {...routeProps}
                    documentInstanceId={parseInt(docInstanceId)}
                    folderId={parseInt(folderId)}
                    jobId={parseInt(jobId)}
                    mediaType={mediaTypeParam}
                    templateDocumentInstanceId={parseInt(templateDocumentInstanceId)}
                    templateType={templateTypeParam}
                    onDocumentDeleted={props.onDocumentDeleted}
                    callingFromEntityId={callingFromEntityId}
                    initialActiveTab={initialActiveTab}
                />
            );
        case MediaType.Photo:
            const { handler: _handler, ...photoProps } = props;
            return (
                <Photo
                    {...photoProps}
                    {...routeProps}
                    documentInstanceId={parseInt(docInstanceId)}
                    jobId={parseInt(jobId)}
                    initialActiveTab={initialActiveTab}
                />
            );
        case MediaType.Video:
            return (
                <Video
                    {...props}
                    {...routeProps}
                    documentInstanceId={parseInt(docInstanceId)}
                    jobId={parseInt(jobId)}
                    initialActiveTab={initialActiveTab}
                />
            );
        default:
            return undefined;
    }
};

export const RouteMediaProperties = (
    props: Omit<
        ComponentProps<typeof Video>,
        "documentInstanceId" | "jobId" | "history" | "location" | "match" | "initialActiveTab"
    >,
) => (
    <RouteRelative
        path={routes.media.mediaProperties}
        render={(routeProps: RouteComponentProps<IMediaRouteParams>) => (
            <BTSuspense>{getComponent(props, routeProps)}</BTSuspense>
        )}
    />
);

export const RouteMediaPropertiesFromAttachedFiles = (
    props: IRouteMediaPropertiesProps & {
        entityName: string;
    },
) => {
    const { entityName } = props;

    return (
        <RouteRelative
            path={`/Attachment/${entityName}/:entityId${routes.media.mediaProperties}`}
            render={(routeProps: RouteComponentProps<IMediaRouteParams>) => (
                <BTSuspense>{getComponent(props, routeProps)}</BTSuspense>
            )}
        />
    );
};
